/**
 * Axios instance — single source of truth for all HTTP from the client.
 *
 * Key behaviours:
 *
 * 1. X-Business-Id header is attached when a business is active. When
 *    activeBusinessSlug === null (Group Mode signal), the header is
 *    OMITTED so the backend's business middleware returns cross-business
 *    data (group reports etc). The id is read from the persisted
 *    activeBusinessId first (survives a refresh before availableBusinesses
 *    has refetched), falling back to resolving the slug against the list.
 *
 * 2. Pre-auth request gate (Round 5 fix): blocks API calls before a
 *    bearer token is set in the auth store. Prevents the request storm
 *    where /auth/me + /activity + /notifications all fired during the
 *    login transition.
 *
 * 3. Loop-safe 401 handler: auth endpoints are excluded from auto-logout
 *    redirect to avoid infinite loops when login itself returns 401.
 *
 * 4. 402 handler (subscription gate SUBSCRIPTION_EXPIRED / SUBSCRIPTION_REQUIRED,
 *    plan limits PLAN_LIMIT, features outside the plan ADDON_REQUIRED): a
 *    response is turned into a human message (attached as error.friendlyMessage)
 *    and broadcast as an `eaze:subscription-blocked` window event so the UI can
 *    surface the modal/wall instead of a raw "402" toast.
 *
 * 5. Idempotency-Key header is automatically copied if set on the
 *    request config so endpoints like csv-import/products/commit can
 *    use simple `api.post(url, body, { headers: { 'Idempotency-Key': k }})`.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import useAuthStore from "@/stores/authStore";
import { friendlyErrorMessage } from "@/utils/friendly-error";
import useBusinessStore from "@/stores/businessStore";

const BASE_URL =
  (import.meta as ImportMeta & { env: Record<string, string> }).env
    .VITE_API_BASE_URL || "http://localhost:3000";

// Endpoints that are PUBLIC — no token required, never trigger 401 handling
const PUBLIC_ENDPOINTS = [
  "/auth/login",
  "/auth/mfa/verify", // second step of sign-in (no access token yet)
  "/auth/phone/request", // phone sign-in (SMS code)
  "/auth/phone/verify",
  "/auth/refresh-token",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify",
  "/onboarding/",
  "/subscription-plans/public",
  "/user-invitations/public",
  "/receipts/r/", // public receipt view endpoint
  "/leads", // book a demo / contact sales (website visitors aren't signed in)
  "/referrals/", // referral code check at sign-up
  "/share/", // shared documents
  "/public/statements/", // a customer's statement link
  "/public/unsubscribe", // unsubscribe link in marketing emails
  "/public/locale", // visitor country → currency (landing, sign-up)
  "/public/enterprise-quotes/", // enterprise proposal link
  "/public/pay/", // Eaze pay links
  "/public/quotes/", // a customer's quotation page
];

function isPublic(url?: string | null): boolean {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some((p) => url.includes(p));
}

const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── REQUEST INTERCEPTOR ─────────────────────────────────────────

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const url = config.url || "";

    // 1. Auth header — attach token if we have one, skip for public endpoints
    if (!isPublic(url)) {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Pre-auth gate: block the request entirely. Returning a rejected
        // promise here prevents a flood of unauth'd calls during the
        // login transition. Public endpoints bypass this above.
        return Promise.reject(
          Object.assign(new Error("Not authenticated"), {
            config,
            response: { status: 401, data: { message: "Not authenticated" } },
            isAxiosError: true,
            __preAuthBlocked: true,
          }),
        );
      }
    }

    // 2. X-Business-Id — attach ONLY when a business is active.
    //    Group Mode signal = activeBusinessSlug === null → omit header.
    //    Header is also omitted for public endpoints.
    if (!isPublic(url)) {
      const businessState = useBusinessStore.getState();
      const slug = businessState.activeBusinessSlug;

      // Group Mode: slug explicitly null → omit header on purpose so the
      // backend returns cross-business data.
      if (slug) {
        // Prefer the persisted resolved id — survives a refresh even before
        // availableBusinesses has refetched. This is what fixed the burst of
        // 400s ("requires businessId") seen right after a page reload.
        let businessId: string | undefined =
          businessState.activeBusinessId ?? undefined;

        // Fallback: resolve slug → id from the list when the id isn't set
        // yet (e.g. first selection before persistence catches up).
        if (!businessId && Array.isArray(businessState.availableBusinesses)) {
          const business = businessState.availableBusinesses.find(
            (b) => b?.slug === slug || b?._id === slug,
          );
          businessId = business?._id || business?.id;
        }

        // A caller that names its business (the offline outbox sending a
        // sale made in another business) keeps it.
        if (businessId && !config.headers["X-Business-Id"]) {
          config.headers["X-Business-Id"] = businessId;
          // The branch they're working at, if they picked one (server checks it).
          const branchId = businessState.activeBranchByBusiness?.[businessId];
          if (branchId && !config.headers["X-Branch-Id"]) config.headers["X-Branch-Id"] = branchId;
        }
      }
      // All-businesses mode: a page showing one business (its own business
      // bar) sends that business; otherwise no header → cross-business read.
      else if (businessState.pageBusinessId && !config.headers["X-Business-Id"]) {
        config.headers["X-Business-Id"] = businessState.pageBusinessId;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── RESPONSE INTERCEPTOR ─────────────────────────────────────────

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Pre-auth blocks aren't real errors — silently reject without logout
    if ((error as { __preAuthBlocked?: boolean }).__preAuthBlocked) {
      return Promise.reject(error);
    }

    // Every caller that shows error.message now shows plain words instead
    // of "Request failed with status code 422" or database text.
    error.message = friendlyErrorMessage(error);

    const status = error.response?.status;
    const url = error.config?.url || "";

    // 402 — subscription gate. Surface a human message + signal the UI,
    // instead of letting a raw "402" reach a toast.
    if (status === 402) {
      const data = error.response?.data as
        | { code?: string; message?: string }
        | undefined;
      const friendly =
        data?.message ||
        (data?.code === "SUBSCRIPTION_REQUIRED"
          ? "No active subscription. Choose a plan to continue."
          : "Your subscription has expired — please renew to continue.");
      // Let any interested UI (banner/modal/wall) react without coupling.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("eaze:subscription-blocked", {
            detail: { code: data?.code, message: friendly },
          }),
        );
      }
      // Attach a clean message so callers/toasts show this, not "402".
      (error as AxiosError & { friendlyMessage?: string }).friendlyMessage =
        friendly;
    }

    // 403 MFA_SETUP_REQUIRED: the business requires 2-step sign-in and this
    // person hasn't set it up — send them to set it up.
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (status === 403 && code === "MFA_SETUP_REQUIRED") {
      try {
        useAuthStore.setState({ mfaSetupRequired: true });
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/security/two-step")) {
        window.location.assign("/security/two-step");
      }
      return Promise.reject(error);
    }

    // 401 on a signed-in call: the short access token ran out. Swap the
    // refresh token for a new one (once) and repeat the request.
    const cfg = error.config as (InternalAxiosRequestConfig & { __retried?: boolean }) | undefined;
    if (status === 401 && !isPublic(url) && cfg && !cfg.__retried && !url.includes("/auth/refresh-token") && !url.includes("/auth/logout") && useAuthStore.getState().refreshToken) {
      cfg.__retried = true;
      return useAuthStore
        .getState()
        .refreshAuthToken()
        .then((ok) => {
          if (!ok) throw error;
          cfg.headers.Authorization = `Bearer ${useAuthStore.getState().token}`;
          return api.request(cfg);
        })
        .catch((e) => {
          if (e === error) signOutAndGoToLogin();
          return Promise.reject(e);
        });
    }

    // 401 handling — but ONLY for non-auth endpoints. Logging in itself
    // can return 401; treating that as "log out and redirect to login"
    // would cause an infinite loop.
    if (status === 401 && !isPublic(url)) {
      // Token is bad or expired — clear it.
      try {
        useAuthStore.getState().logout?.();
      } catch {
        /* ignore */
      }
      // Single redirect, only if we're not already on the login page.
      // Hard navigation so React state resets cleanly.
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/login")
      ) {
        window.location.assign("/login");
      }
    }

    return Promise.reject(error);
  },
);

function signOutAndGoToLogin() {
  try {
    useAuthStore.getState().logout?.();
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) window.location.assign("/login");
}

export default api;
